import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { MapRef } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import logExploreFiltersApplied from '@alltrails/amplitude/events/logExploreFiltersApplied';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getHelpCenterUrl from '@alltrails/core/utils/getHelpCenterUrl';
import FilterBar, { CustomFilterChip, FilterConfig } from '@alltrails/denali/components/FilterBar';
import Link from '@alltrails/denali/components/Link';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { usePageWidth } from '@alltrails/denali/hooks/usePageWidth';
import Filter from '@alltrails/denali/icons/Filter';
import Modal from '@alltrails/modal/components/Modal/Modal';
import type Context from '@alltrails/types/Context';
import useActivityChipsConfig from '../../hooks/filterConfigs/useActivityChipsConfig';
import useDifficultyChipsConfig from '../../hooks/filterConfigs/useDifficultyChipsConfig';
import useDistanceAwayFilterConfig, {
  distanceAwayFilterId,
  getDefaultDistanceAwayMeters
} from '../../hooks/filterConfigs/useDistanceAwayFilterConfig';
import useDurationFilterConfig from '../../hooks/filterConfigs/useDurationFilterConfig';
import useElevationGainFilterConfig from '../../hooks/filterConfigs/useElevationGainFilterConfig';
import useFeatureChipsConfig from '../../hooks/filterConfigs/useFeatureChipsConfig';
import useHighestPointFilterConfig from '../../hooks/filterConfigs/useHighestPointFilterConfig';
import useLengthFilterConfig from '../../hooks/filterConfigs/useLengthFilterConfig';
import useMinRatingFilterConfig from '../../hooks/filterConfigs/useMinRatingFilterConfig';
import useRouteTypeFilterConfig from '../../hooks/filterConfigs/useRouteTypeFilterConfig';
import useSuitabilityFilterConfig from '../../hooks/filterConfigs/useSuitabilityFilterConfig';
import useTrailEngagementFilterConfig from '../../hooks/filterConfigs/useTrailEngagementFilterConfig';
import useTrailTrafficFilterConfig from '../../hooks/filterConfigs/useTrailTrafficFilterConfig';
import useAdminFiltersModal from '../../hooks/useAdminFiltersModal';
import useAppliedFilterPills from '../../hooks/useAppliedFilterPills';
import useDistanceAwayFilter from '../../hooks/useDistanceAwayFilter';
import useExploreServiceFilterCounts from '../../hooks/useExploreServiceFilterCounts';
import useExploreServiceFilterState from '../../hooks/useExploreServiceFilterState';
import useFilterBarScroll from '../../hooks/useFilterBarScroll';
import useUnitAwareExploreFilters from '../../hooks/useUnitAwareExploreFilters';
import { ExploreServiceState, updateAllFiltersModalOpen } from '../../redux/exploreServiceSlice';
import buildExploreFiltersAppliedParams, { isDefaultFilterState } from '../../utils/buildExploreFiltersAppliedParams';
import AllFiltersModal, { formatRatingValue } from '../AllFiltersModal/AllFiltersModal';
import styles from '../AllFiltersModal/AllFiltersModal.module.scss';
import DifficultyInfoModalContent from '../DifficultyInfoModalContent/DifficultyInfoModalContent';
import DistanceAwayFilterMobilePopup from '../DistanceAwayFilterMobilePopup/DistanceAwayFilterMobilePopup';
import WheelchairInfoModalContent from '../WheelchairInfoModalContent/WheelchairInfoModalContent';
import buildChipsDropdownConfig from './buildChipsDropdownConfig';
import buildRangeSliderDropdownConfig from './buildRangeSliderDropdownConfig';

function orderFiltersByApplied(filterIds: string[], appliedFilterIds: string[] = []): string[] {
  // Create a Set from arrayB for efficient O(1) lookups.
  const appliedFilterIdsSet = new Set(appliedFilterIds);

  // Separate elements into two groups: present in B, and not present in B.
  const present: string[] = [];
  const notPresent: string[] = [];

  for (const item of filterIds) {
    if (appliedFilterIdsSet.has(item)) {
      present.push(item);
    } else {
      notPresent.push(item);
    }
  }

  // Concatenate the two groups, with elements present in B coming first.
  return present.concat(notPresent);
}

type ExploreFiltersBarProps = {
  className?: string;
  filtersContainerClassName?: string;
  onDistanceAwayFilterClose?: () => void;
  onDistanceAwayFilterOpen?: () => void;
  map?: MapRef;
};

const ExploreFiltersBar = ({
  className,
  filtersContainerClassName,
  onDistanceAwayFilterClose,
  onDistanceAwayFilterOpen,
  map
}: ExploreFiltersBarProps) => {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const isMobile = useIsMobileSizedScreen();
  const pageWidth = usePageWidth() ?? 'md';
  const dispatch = useDispatch();
  const { naturalLanguageQuery, useMetric, exploreFiltersAppliedState } = useSelector(
    (state: { context: Context; exploreService: ExploreServiceState }) => ({
      naturalLanguageQuery: state.exploreService.naturalLanguageQuery,
      useMetric: state.context.displayMetric,
      exploreFiltersAppliedState: {
        boundingBox: state.exploreService.boundingBox,
        distanceAway: state.exploreService.distanceAway,
        filters: state.exploreService.filters,
        searchResultsSummary: state.exploreService.searchResultsSummary,
        sort: state.exploreService.sort,
        trailEngagement: state.exploreService.trailEngagement,
        useMetric: state.context.displayMetric,
        searchOrigin: state.exploreService.naturalLanguageSearchAnalyticData?.searchOrigin
      }
    })
  );
  const latestExploreFiltersAppliedStateRef = useRef(exploreFiltersAppliedState);

  useEffect(() => {
    latestExploreFiltersAppliedStateRef.current = exploreFiltersAppliedState;
  }, [exploreFiltersAppliedState]);

  const { filterCount } = useExploreServiceFilterCounts();
  useUnitAwareExploreFilters(useMetric);
  const [isDistanceAwayFilterMobilePopupOpen, setIsDistanceAwayFilterMobilePopupOpen] = useState(false);
  // Scroll behavior (edge fade + close-open-dropdown-on-scroll) for the horizontally-scrolling filter row.
  const { wrapperRef, filterBarRef, filtersContainerClassName: scrollContainerClassName, onDropdownOpen, onDropdownClose } = useFilterBarScroll();

  const onFilterClose = useCallback(
    (filterId: string) => {
      onDropdownClose();
      if (filterId === distanceAwayFilterId) {
        onDistanceAwayFilterClose?.();
      }
    },
    [onDropdownClose, onDistanceAwayFilterClose]
  );
  const closeDistanceAwayFilterMobilePopup = useCallback(() => {
    setIsDistanceAwayFilterMobilePopupOpen(false);
    onFilterClose(distanceAwayFilterId);
  }, [onFilterClose]);

  const onFilterOpen = useCallback(
    (filterId: string) => {
      onDropdownOpen();
      if (filterId === distanceAwayFilterId && onDistanceAwayFilterOpen) {
        onDistanceAwayFilterOpen();
      } else if (filterId !== distanceAwayFilterId && isDistanceAwayFilterMobilePopupOpen) {
        closeDistanceAwayFilterMobilePopup();
      }
    },
    [onDropdownOpen, closeDistanceAwayFilterMobilePopup, isDistanceAwayFilterMobilePopupOpen, onDistanceAwayFilterOpen]
  );
  const openDistanceAwayFilterMobilePopup = useCallback(() => {
    setIsDistanceAwayFilterMobilePopupOpen(true);
    onFilterOpen(distanceAwayFilterId);
  }, [onFilterOpen]);

  const [isAllFiltersModalOpen, setIsAllFiltersModalOpen] = useState(false);
  const onAllFiltersModalOpen = useCallback(() => {
    setIsAllFiltersModalOpen(true);
    dispatch(updateAllFiltersModalOpen(true));
  }, [dispatch]);
  const onAllFiltersModalCloseRequest = useCallback(() => {
    dispatch(updateAllFiltersModalOpen(false));
    setIsAllFiltersModalOpen(false);
  }, [dispatch]);
  const onAllFiltersSeeTrailsClick = useCallback(() => {
    const state = latestExploreFiltersAppliedStateRef.current;
    if (!isDefaultFilterState(state)) {
      logExploreFiltersApplied(buildExploreFiltersAppliedParams(state));
    }
  }, []);

  const [isDifficultyInfoModalOpen, setIsDifficultyInfoModalOpen] = useState(false);
  const difficultyLink = useMemo(
    () => (
      <Link noUnderline onClick={() => setIsDifficultyInfoModalOpen(true)} size="sm" variant="secondary">
        <FormattedMessage defaultMessage="About difficulty" />
      </Link>
    ),
    []
  );

  const defaultDistanceAway = useMemo(() => getDefaultDistanceAwayMeters(useMetric), [useMetric]);
  const { applyDistanceAway, distanceAway, distanceAwaySliderSubHeader, localDistanceAway, setLocalDistanceAway } = useDistanceAwayFilter(
    map,
    defaultDistanceAway
  );

  const onDistanceAwayChangeEnd = useMemo(() => {
    if (distanceAway?.distance) {
      // When a distance away is already applied, update that filter when a user finished dragging the slider
      return (newDistance: number | undefined) => applyDistanceAway(newDistance);
    } else {
      return undefined;
    }
  }, [applyDistanceAway, distanceAway?.distance]);

  const onDistanceAwayApply = useMemo(() => {
    if (distanceAway?.distance) {
      // Don't show an apply button if a distance away is already applied
      return undefined;
    } else {
      return () => applyDistanceAway(localDistanceAway);
    }
  }, [applyDistanceAway, distanceAway?.distance, localDistanceAway]);

  const onDistanceAwayClear = useMemo(() => {
    if (distanceAway?.distance) {
      return () => {
        applyDistanceAway(undefined);
        setLocalDistanceAway(defaultDistanceAway);
      };
    } else {
      return undefined;
    }
  }, [applyDistanceAway, defaultDistanceAway, distanceAway?.distance, setLocalDistanceAway]);

  const distanceAwayFilter = useDistanceAwayFilterConfig({
    appliedDistanceAway: distanceAway?.distance,
    distanceAway: localDistanceAway,
    hideAnyOption: true,
    onApplyClick: onDistanceAwayApply,
    onClear: onDistanceAwayClear,
    onDistanceAwayChange: setLocalDistanceAway,
    onDistanceAwayChangeEnd,
    subHeader: distanceAwaySliderSubHeader,
    useMetric
  });

  const distanceAwayCustomFilterChip = useMemo<CustomFilterChip | undefined>(() => {
    // On mobile, we don't show distance away as a normal filter that uses the BottomSheet.
    // Instead, we show a popup that allows the user to move the map around to set the DA center.
    // To handle this, convert the slider filter config into a custom filter config that launches the popup.
    if (!isMobile) {
      return undefined;
    }
    return {
      type: 'custom-chip',
      id: distanceAwayFilterId,
      filterChip: {
        ...distanceAwayFilter.filterChip,
        isOpen: isDistanceAwayFilterMobilePopupOpen,
        onClick: isDistanceAwayFilterMobilePopupOpen ? closeDistanceAwayFilterMobilePopup : openDistanceAwayFilterMobilePopup
      }
    };
  }, [
    closeDistanceAwayFilterMobilePopup,
    distanceAwayFilter.filterChip,
    isDistanceAwayFilterMobilePopupOpen,
    isMobile,
    openDistanceAwayFilterMobilePopup
  ]);

  const [isWheelchairInfoModalOpen, setIsWheelchairInfoModalOpen] = useState(false);
  const wheelchairLink = useMemo(
    () => (
      <Link noUnderline onClick={() => setIsWheelchairInfoModalOpen(true)} size="sm" variant="secondary">
        <FormattedMessage defaultMessage="Learn more" />
      </Link>
    ),
    []
  );

  const { exploreServiceFilters, onFiltersChange, onTrailEngagementChanged, trailEngagement } = useExploreServiceFilterState();

  const onFiltersChangeWithLog = useCallback(
    (newFilters: Parameters<typeof onFiltersChange>[0]) => {
      onFiltersChange(newFilters);
    },
    [onFiltersChange]
  );
  const onTrailEngagementChangedWithLog = useCallback(
    (newTrailEngagement: Parameters<typeof onTrailEngagementChanged>[0]) => {
      onTrailEngagementChanged(newTrailEngagement);
    },
    [onTrailEngagementChanged]
  );

  const difficultyChips = useDifficultyChipsConfig(exploreServiceFilters, onFiltersChangeWithLog);
  const activityChips = useActivityChipsConfig(exploreServiceFilters, onFiltersChangeWithLog);
  const featureChips = useFeatureChipsConfig(exploreServiceFilters, onFiltersChangeWithLog);
  const difficultyFilter = buildChipsDropdownConfig(difficultyChips, exploreServiceFilters, onFiltersChangeWithLog, difficultyLink);
  const activityFilter = buildChipsDropdownConfig(activityChips, exploreServiceFilters, onFiltersChangeWithLog);
  const featureFilter = buildChipsDropdownConfig(featureChips, exploreServiceFilters, onFiltersChangeWithLog);

  const lengthFilter = useLengthFilterConfig(exploreServiceFilters, onFiltersChangeWithLog, useMetric);
  const elevationGainFilter = useElevationGainFilterConfig(exploreServiceFilters, onFiltersChangeWithLog, useMetric);
  const durationFilter = useDurationFilterConfig(exploreServiceFilters, onFiltersChangeWithLog);
  const suitabilityFilter = useSuitabilityFilterConfig(exploreServiceFilters, onFiltersChangeWithLog, wheelchairLink);
  const routeTypeFilter = useRouteTypeFilterConfig(exploreServiceFilters, onFiltersChangeWithLog);
  const minRatingFilter = useMinRatingFilterConfig(exploreServiceFilters, onFiltersChangeWithLog, formatRatingValue);
  const trailTrafficFilter = useTrailTrafficFilterConfig(exploreServiceFilters, onFiltersChangeWithLog);
  const highestPointFilter = useHighestPointFilterConfig(exploreServiceFilters, onFiltersChangeWithLog, useMetric);
  const trailEngagementFilter = useTrailEngagementFilterConfig(trailEngagement, onTrailEngagementChangedWithLog);

  const { adminFiltersChip, adminFiltersModal } = useAdminFiltersModal();

  // Applied filters shown as individual removable pills, between the "All" chip and the default list.
  const appliedFilterPills = useAppliedFilterPills({
    filters: exploreServiceFilters,
    rangeFilters: {
      length: lengthFilter,
      elevationGain: elevationGainFilter,
      highestPoint: highestPointFilter,
      duration: durationFilter,
      distanceAway: distanceAwayFilter
    },
    trailEngagement,
    onFiltersChange: onFiltersChangeWithLog,
    onTrailEngagementChange: onTrailEngagementChangedWithLog
  });

  const filterBarFilters = useMemo(
    (): (CustomFilterChip | FilterConfig)[] => [
      difficultyFilter,
      buildRangeSliderDropdownConfig(lengthFilter, intl),
      buildRangeSliderDropdownConfig(elevationGainFilter, intl),
      activityFilter,
      featureFilter,
      highestPointFilter,
      durationFilter,
      distanceAwayCustomFilterChip ?? distanceAwayFilter,
      routeTypeFilter,
      suitabilityFilter,
      minRatingFilter,
      trailTrafficFilter,
      ...(user ? [trailEngagementFilter] : [])
    ],
    [
      intl,
      distanceAwayCustomFilterChip,
      distanceAwayFilter,
      activityFilter,
      difficultyFilter,
      lengthFilter,
      durationFilter,
      elevationGainFilter,
      suitabilityFilter,
      featureFilter,
      routeTypeFilter,
      minRatingFilter,
      trailTrafficFilter,
      highestPointFilter,
      user,
      trailEngagementFilter
    ]
  );

  const renderedFilterBarFilters = useMemo(() => {
    if (naturalLanguageQuery) {
      const maxFilters = pageWidth === 'md' ? 1 : 5;
      const selectedFilters: (CustomFilterChip | FilterConfig)[] = [];

      // Core filters that should always be available as fallbacks
      const coreFilterIds = new Set(['distance-away', 'activity', 'difficulty', 'length']);
      const appliedFilters: (CustomFilterChip | FilterConfig)[] = [];
      const coreFilters: (CustomFilterChip | FilterConfig)[] = [];
      for (const filter of filterBarFilters) {
        const hasSelectedItems = filter.filterChip.selectedItems && filter.filterChip.selectedItems.length > 0;
        if (hasSelectedItems) {
          appliedFilters.push(filter);
        } else if (coreFilterIds.has(filter.id)) {
          coreFilters.push(filter);
        }
      }

      // Applied filters first, then core filters to fill the remaining slots
      selectedFilters.push(...appliedFilters.slice(0, maxFilters));
      if (selectedFilters.length < maxFilters) {
        selectedFilters.push(...coreFilters.slice(0, maxFilters - selectedFilters.length));
      }

      const exploreServiceFilterIds = Object.keys(exploreServiceFilters);
      if (distanceAway) {
        exploreServiceFilterIds.push(distanceAwayFilterId);
      }
      const orderedFilterIds = orderFiltersByApplied(
        selectedFilters.map(filter => filter.id),
        exploreServiceFilterIds
      );
      selectedFilters.sort((a, b) => orderedFilterIds.indexOf(a.id) - orderedFilterIds.indexOf(b.id));

      const allText = intl.formatMessage({ defaultMessage: 'All' });
      const appliedFilterCountText = !!filterCount && filterCount > 0 ? ` (${filterCount})` : '';
      const nlsAllFiltersChip: CustomFilterChip = {
        type: 'custom-chip',
        id: 'all-filters',
        filterChip: {
          hideChevron: true,
          icon: { Component: Filter },
          onClick: () => {
            onAllFiltersModalOpen();
            if (isDistanceAwayFilterMobilePopupOpen) {
              closeDistanceAwayFilterMobilePopup();
            }
          },
          unselectedText: `${allText}${appliedFilterCountText}`
        }
      };
      selectedFilters.unshift(nlsAllFiltersChip);
      if (adminFiltersChip) {
        selectedFilters.push(adminFiltersChip);
      }
      return selectedFilters;
    }

    // [All] → default primary filters → applied-filter pills
    const maxDefaultFilters = pageWidth === 'md' ? 1 : 5;
    const allText = intl.formatMessage({ defaultMessage: 'All' });
    const allFiltersChip: CustomFilterChip = {
      type: 'custom-chip',
      id: 'all-filters',
      filterChip: {
        hideChevron: true,
        icon: { Component: Filter },
        selectedItems: filterCount ? [allText] : undefined,
        onClick: () => {
          onAllFiltersModalOpen();
          if (isDistanceAwayFilterMobilePopupOpen) {
            closeDistanceAwayFilterMobilePopup();
          }
        },
        unselectedText: allText
      }
    };

    // Default pills stay generic (label only, no value/clear) — applied state is shown by the pills above.
    const defaultFilters = filterBarFilters
      .slice(0, maxDefaultFilters)
      .map(filter =>
        filter.type === 'custom-chip' ? filter : { ...filter, filterChip: { ...filter.filterChip, onClear: undefined, selectedItems: undefined } }
      );

    const result: (CustomFilterChip | FilterConfig)[] = [allFiltersChip, ...defaultFilters, ...appliedFilterPills];
    if (adminFiltersChip) {
      result.push(adminFiltersChip);
    }
    return result;
  }, [
    naturalLanguageQuery,
    pageWidth,
    intl,
    filterCount,
    adminFiltersChip,
    exploreServiceFilters,
    distanceAway,
    filterBarFilters,
    appliedFilterPills,
    onAllFiltersModalOpen,
    isDistanceAwayFilterMobilePopupOpen,
    closeDistanceAwayFilterMobilePopup
  ]);

  return (
    <>
      <div ref={wrapperRef}>
        <FilterBar
          ref={filterBarRef}
          className={className}
          filters={renderedFilterBarFilters}
          filtersContainerClassName={classNames(filtersContainerClassName, scrollContainerClassName)}
          onFilterClose={onFilterClose}
          onFilterOpen={onFilterOpen}
          // Portal the dropdowns so they render outside the mask-faded filters container (which would otherwise clip them).
          usePortalForDropdowns
        />
      </div>
      <AllFiltersModal
        map={map}
        onCloseRequest={onAllFiltersModalCloseRequest}
        onSeeTrailsClick={onAllFiltersSeeTrailsClick}
        isOpen={isAllFiltersModalOpen}
      />
      {adminFiltersModal}
      <Modal
        isOpen={isDifficultyInfoModalOpen}
        onCloseRequest={() => setIsDifficultyInfoModalOpen(false)}
        title={<FormattedMessage defaultMessage="Difficulty levels" />}
        subtitle={
          <div className={styles.difficultySubtitle}>
            <Typography variant="text200" color="secondary" component="div">
              <FormattedMessage
                defaultMessage="Our team considers <a>many factors</a>, like distance, elevation gain, and trailgoer feedback, to assign a difficulty level to each trail."
                values={{
                  a: chunks => (
                    <Link href={getHelpCenterUrl(languageRegionCode, 16596491196436)} target="_blank" variant="secondary">
                      {chunks}
                    </Link>
                  )
                }}
              />
            </Typography>
            <Typography variant="text200" color="secondary">
              <FormattedMessage defaultMessage="These levels are meant to apply to a wide audience — your experience may differ." />
            </Typography>
          </div>
        }
      >
        <DifficultyInfoModalContent />
      </Modal>
      <Modal
        isOpen={isWheelchairInfoModalOpen}
        onCloseRequest={() => setIsWheelchairInfoModalOpen(false)}
        title={<FormattedMessage defaultMessage="Wheelchair friendly" />}
      >
        <WheelchairInfoModalContent />
      </Modal>
      {isDistanceAwayFilterMobilePopupOpen && (
        <DistanceAwayFilterMobilePopup filterConfig={distanceAwayFilter} onClose={closeDistanceAwayFilterMobilePopup} />
      )}
    </>
  );
};

export default ExploreFiltersBar;
